import type { Grid } from './grid';

/**
 * A flow field toward a single destination cell: at every reachable cell, `dir[i]` names the
 * neighbor (an index into DIRS) that lies on the cheapest path to `destCell`. Built once by
 * Dijkstra from the destination outward, then O(1) per unit per tick to follow.
 */
export interface FlowField {
  destCell: number;
  dist: Uint32Array; // integer cost from the cell to destCell; 0xFFFFFFFF if unreachable
  dir: Int8Array;    // 0..7 index into DIRS (the way to flow), -1 unreachable, -2 at destCell
}

/** The 8 neighbor offsets, index = dir. Orthogonal first, then diagonals. */
export const DIRS: ReadonlyArray<[number, number]> = [
  [0, 1], [0, -1], [1, 0], [-1, 0],
  [1, 1], [1, -1], [-1, 1], [-1, -1],
];

const UNREACHABLE = 0xffffffff;

// Index into DIRS for the offset opposite a given dir index, i.e. oppositeOf[d] points back the way we came.
const oppositeOf: number[] = DIRS.map(([dx, dy]) => DIRS.findIndex(([ox, oy]) => ox === -dx && oy === -dy));

/** A small binary min-heap over (cellIndex, dist), used for Dijkstra. */
class MinHeap {
  private cell: number[] = [];
  private dist: number[] = [];

  get size(): number { return this.cell.length; }

  push(cell: number, dist: number): void {
    this.cell.push(cell); this.dist.push(dist);
    let i = this.cell.length - 1;
    while (i > 0) {
      const p = (i - 1) >> 1;
      if (this.dist[p] <= this.dist[i]) break;
      this.swap(p, i); i = p;
    }
  }

  pop(): { cell: number; dist: number } | undefined {
    const n = this.cell.length;
    if (n === 0) return undefined;
    const topCell = this.cell[0], topDist = this.dist[0];
    const lastCell = this.cell.pop()!, lastDist = this.dist.pop()!;
    if (this.cell.length > 0) {
      this.cell[0] = lastCell; this.dist[0] = lastDist;
      let i = 0;
      const m = this.cell.length;
      for (;;) {
        const l = 2 * i + 1, r = 2 * i + 2;
        let smallest = i;
        if (l < m && this.dist[l] < this.dist[smallest]) smallest = l;
        if (r < m && this.dist[r] < this.dist[smallest]) smallest = r;
        if (smallest === i) break;
        this.swap(i, smallest); i = smallest;
      }
    }
    return { cell: topCell, dist: topDist };
  }

  private swap(a: number, b: number): void {
    const tc = this.cell[a]; this.cell[a] = this.cell[b]; this.cell[b] = tc;
    const td = this.dist[a]; this.dist[a] = this.dist[b]; this.dist[b] = td;
  }
}

/**
 * Dijkstra from `destCell` outward over the grid's passable cells. Diagonal steps cost 1.414×
 * (integer 1414/1000) an orthogonal step's cost; a diagonal step is forbidden when it would cut
 * the corner between two impassable orthogonal neighbors. Impassable cells (cost 0) are never
 * entered.
 */
export function buildFlowField(grid: Grid, destCell: number): FlowField {
  const n = grid.w * grid.h;
  const dist = new Uint32Array(n).fill(UNREACHABLE);
  const dir = new Int8Array(n).fill(-1);

  dist[destCell] = 0;
  dir[destCell] = -2;

  const heap = new MinHeap();
  heap.push(destCell, 0);

  while (heap.size > 0) {
    const top = heap.pop()!;
    const u = top.cell;
    if (top.dist > dist[u]) continue; // stale heap entry
    const ux = grid.cx(u), uy = grid.cy(u);

    for (let d = 0; d < DIRS.length; d++) {
      const [dx, dy] = DIRS[d];
      const vx = ux + dx, vy = uy + dy;
      if (!grid.inBounds(vx, vy)) continue;
      const v = grid.index(vx, vy);
      if (!grid.passable(v)) continue;

      const diagonal = dx !== 0 && dy !== 0;
      if (diagonal) {
        // Forbid cutting the corner between two impassable orthogonal neighbors.
        const orthoA = grid.index(vx, uy);
        const orthoB = grid.index(ux, vy);
        if (!grid.passable(orthoA) && !grid.passable(orthoB)) continue;
      }

      const stepCost = grid.costAt(v) * (diagonal ? 1414 : 1000);
      const nd = top.dist + stepCost;
      if (nd < dist[v]) {
        dist[v] = nd;
        dir[v] = oppositeOf[d]; // from v, the way back to u (toward the destination)
        heap.push(v, nd);
      }
    }
  }

  return { destCell, dist, dir };
}

/** The neighbor cell a unit at `cell` should move toward to follow `ff`, or -1 if none (unreachable or already at destCell). */
export function nextCell(ff: FlowField, grid: Grid, cell: number): number {
  const d = ff.dir[cell];
  if (d < 0) return -1;
  const [dx, dy] = DIRS[d];
  const cx = grid.cx(cell) + dx, cy = grid.cy(cell) + dy;
  if (!grid.inBounds(cx, cy)) return -1;
  return grid.index(cx, cy);
}

/** LRU cache of flow fields keyed by destination cell, so many units sharing an objective reuse one Dijkstra pass. */
export class FlowFieldCache {
  private readonly grid: Grid;
  private readonly max: number;
  private readonly map = new Map<number, FlowField>();

  constructor(grid: Grid, max = 64) {
    this.grid = grid;
    this.max = max;
  }

  get(destCell: number): FlowField {
    const hit = this.map.get(destCell);
    if (hit) {
      // Refresh recency: re-insert at the end (Map preserves insertion order).
      this.map.delete(destCell);
      this.map.set(destCell, hit);
      return hit;
    }
    const ff = buildFlowField(this.grid, destCell);
    this.map.set(destCell, ff);
    if (this.map.size > this.max) {
      const oldest = this.map.keys().next().value;
      if (oldest !== undefined) this.map.delete(oldest);
    }
    return ff;
  }
}
